import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../context/LanguageContext";
import { STATUSES } from "../lib/projectMeta";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const FIELDS = ["number", "client", "address", "contact", "phone", "email", "pm", "installer"];
const EMPTY = { number: "", client: "", address: "", contact: "", phone: "", email: "", pm: "", installer: "", status: "active" };

export function ProjectInfoDialog({ open, onOpenChange, projectId, info, editable, onSaved }) {
    const { t } = useI18n();
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (open) setForm({ ...EMPTY, ...(info || {}) }); }, [open, info]);
    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/projects/${projectId}`, { info: form });
            onSaved?.(form);
            toast.success(t("pj_info_saved"));
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="project-info-dialog" className="bg-[#111827] border-[#1e293b] text-slate-100 max-w-xl" onClick={(e) => e.stopPropagation()}>
                <DialogHeader><DialogTitle className="font-mono">{t("pj_info")}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {FIELDS.map((f) => (
                        <div key={f} className={f === "address" ? "sm:col-span-2" : ""}>
                            <Label className="text-xs text-slate-400">{t(`pj_f_${f}`)}</Label>
                            <Input data-testid={`info-${f}`} value={form[f]} disabled={!editable} onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))}
                                className="mt-1 bg-[#0b0f17] border-[#1e293b] text-slate-100" />
                        </div>
                    ))}
                    <div className="sm:col-span-2">
                        <Label className="text-xs text-slate-400">{t("pj_f_status")}</Label>
                        <Select value={form.status} disabled={!editable} onValueChange={(v) => setForm((s) => ({ ...s, status: v }))}>
                            <SelectTrigger data-testid="info-status" className="mt-1 bg-[#0b0f17] border-[#1e293b] text-slate-100"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                                {STATUSES.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`info-status-${s.key}`}>{t(`pj_st_${s.key}`)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                {editable && (
                    <DialogFooter>
                        <Button data-testid="info-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("pj_save")}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}
